import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from './queryKeys';
import { retryHandlers } from './helpers';
import {
  getOrganizationYaraExclusions,
  addOrganizationYaraExclusion,
  removeOrganizationYaraExclusion,
} from '@kirin/api-client/api/organizations';
import {
  getUserYaraExclusions,
  addUserYaraExclusion,
  removeUserYaraExclusion,
} from '@kirin/api-client/api/account';
import type { yaraExclusions } from '@kirin/api-client/contracts';

/**
 * Hook to fetch YARA excluded extensions (organization or user)
 */
export function useYaraExclusions(
  organizationId: string | undefined,
  enabled: boolean = true
) {
  const isPersonal = !organizationId;

  return useQuery({
    queryKey: isPersonal
      ? queryKeys.yaraExclusions.user()
      : queryKeys.yaraExclusions.organization(organizationId),
    queryFn: () =>
      isPersonal
        ? getUserYaraExclusions(apiClient)
        : getOrganizationYaraExclusions(apiClient, organizationId),
    enabled: enabled, // Run query if enabled (organizationId can be undefined for personal)
    retry: retryHandlers.detail, // Don't retry on 404/auth errors
  });
}

/**
 * Hook to add extension to YARA exclusion list (organization or user)
 */
export function useAddYaraExclusion(organizationId: string | undefined) {
  const queryClient = useQueryClient();
  const isPersonal = !organizationId;

  return useMutation({
    mutationFn: (data: yaraExclusions.AddYaraExclusionBody) =>
      isPersonal
        ? addUserYaraExclusion(apiClient, data)
        : addOrganizationYaraExclusion(apiClient, organizationId, data),
    onSuccess: () => {
      // Invalidate and refetch exclusions list
      queryClient.invalidateQueries({
        queryKey: isPersonal
          ? queryKeys.yaraExclusions.user()
          : queryKeys.yaraExclusions.organization(organizationId),
      });
    },
  });
}

/**
 * Hook to remove extension from YARA exclusion list (organization or user)
 */
export function useRemoveYaraExclusion(organizationId: string | undefined) {
  const queryClient = useQueryClient();
  const isPersonal = !organizationId;

  return useMutation({
    mutationFn: (extensionId: string) =>
      isPersonal
        ? removeUserYaraExclusion(apiClient, extensionId)
        : removeOrganizationYaraExclusion(
            apiClient,
            organizationId,
            extensionId
          ),
    onSuccess: () => {
      // Invalidate and refetch exclusions list
      queryClient.invalidateQueries({
        queryKey: isPersonal
          ? queryKeys.yaraExclusions.user()
          : queryKeys.yaraExclusions.organization(organizationId),
      });
    },
  });
}
